import React, { useEffect, useState, useRef } from 'react';
import { Tooltip, Typography, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, TableRowLabel, TableFooterPagination, TableFooterShowMore, useBreakpoint, Badge, } from '@one-thd/sui-atomic-components';
import { Mobile, TextToConfirm, QRCode } from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import { DEFAULT_ROWS_PER_PAGE, MAX_ROWS_PER_PAGE, PAGINATION_THRESHOLD_VALUE, } from '../../../constants/payments-table';
import { cardSortByMap } from '../../../constants/sort';
import { MeatballsMenu } from '../../shared/meatballs-menu/meatballs-menu';
import { getCardImagePath } from '../../../utils/credit-cards';
import { getLastModifiedDate } from '../../../utils/date';
import { usePayments } from '../../../hooks/use-payments';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { ACTIVE_SAVES_MSG, CHANGES_SAVED_MSG, DELETED_CARD_MSG, DELETED_CITI_CARDS_SUCCESS_MSG1, DELETED_CITI_CARDS_SUCCESS_MSG2, HD_PASS_SUCCESS_STOP_AUTH_MSG, INACTIVE_SAVES_MSG, } from '../../../constants/response-success-messages';
import { DELETED_CITI_CARDS_ERROR_MSG1, DELETED_CITI_CARDS_ERROR_MSG2, ERR_GENERIC, ERR_UNABLE_TO_SAVE, } from '../../../constants/response-error-messages';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';
import { getPaymentModalsVersion, scrollToTop } from '../../../utils/general';
import { InfoPopover } from '../../shared/popover/popover';
import { CardType } from '../../../constants/payments';
import { setCardSortAnalytics, setTableViewPaginationAnalytics, } from '../../../utils/payments-analytics';
import { CustomerRole } from '../../../models/common/customer';
export const TableView = ({ triggerGetPaymentCards, onChangePage, onActionFailure, onActionSuccess, canShowHDWallet, }) => {
    const { creditCards, paginationInfo, currentSortByValue, currentPageNumber, submittedSearchType, submittedSearchValue, isSearchActive, } = usePayments();
    const [isFooterDropdownOpen, setIsFooterDropdownOpen] = useState(false);
    const tableFooterRef = useRef(null);
    const { role } = useCustomerInfo();
    const { onSetActive, onSetInactive, onViewPurchases, onEditCreditCard, onDeleteCreditCard, onDefaultCreditCardChange, onCreditCardRemotePurchases, onHomeDepotWallet, onRemoveHDWallet, } = useMeatballMenuActions();
    const sortDirection = currentSortByValue.ascendingOrder ? 'asc' : 'desc';
    const paymentCards = creditCards === null || creditCards === void 0 ? void 0 : creditCards.paymentCards;
    const screen = useBreakpoint('lg');
    const isSmallScreen = screen.lesser;
    const CELL_WIDTH = {
        CARD: 170,
        NICKNAME: 140,
        EXPIRATION: 118,
        STATUS: 109,
        REMOTE_PURCHASES: 116,
        HD_PASS: 40,
        ADDED_BY: 147,
        LAST_EDITED: 104,
        ACTIONS: 80,
    };
    const isRemoveHDPassEnabled = !!useConfigService('ENABLE_HDPASS_MENU_REMOVE');
    const creditCardsCount = (isSearchActive
        ? paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalSearchedCreditCards
        : paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCreditCards) || 0;
    const currentPageNumberTable = currentPageNumber - 1;
    const shouldShowPagination = creditCardsCount > DEFAULT_ROWS_PER_PAGE;
    const paginationClasses = [
        `sui-border-solid sui-border-primary sui-border-b-1`,
        'sui-border-x-1 sui-border-primary sui-text-left sui-px-4 sui-py-2',
    ].join(' ');
    const createdBySortDirection = currentSortByValue.sortBy === 'Added By' ? sortDirection : null;
    const cardNumberSortDirection = currentSortByValue.sortBy === 'Card Number' ? sortDirection : null;
    const expDateSortDirection = currentSortByValue.sortBy === 'Expiration Date' ? sortDirection : null;
    const lastEditedSortDirection = currentSortByValue.sortBy === 'Last Edited Date' ||
        !currentSortByValue.sortBy
        ? sortDirection
        : null;
    const nicknameSortDirection = currentSortByValue.sortBy === 'Nickname' ? sortDirection : null;
    const statusSortDirection = currentSortByValue.sortBy === 'Status' ? sortDirection : null;
    const [numberOfRowsPerPage, setNumberOfRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    useEffect(() => {
        // Note: Remove when stencil TableFooterPagination will has a prop for custom dropdown
        const hideViewAllOption = () => {
            if (isFooterDropdownOpen &&
                tableFooterRef.current &&
                creditCardsCount > MAX_ROWS_PER_PAGE) {
                const dropdownOptions = tableFooterRef.current.querySelectorAll('ul[role="listbox"] > li');
                const viewAllOption = dropdownOptions[dropdownOptions.length - 1];
                if (viewAllOption.textContent &&
                    /view all/i.test(viewAllOption.textContent)) {
                    viewAllOption.classList.add('sui-hidden');
                }
            }
        };
        hideViewAllOption();
    }, [isFooterDropdownOpen, creditCardsCount, tableFooterRef]);
    const onPageChangeHandler = (_event, newPage) => {
        setTableViewPaginationAnalytics({
            rows: numberOfRowsPerPage,
            isSearchActive,
            filterType: submittedSearchType,
            currentPageNumber: newPage + 1,
        });
        onChangePage(newPage + 1);
    };
    const onRowsPerPageChangeHandler = (_event, rows) => {
        setTableViewPaginationAnalytics({
            rows,
            isSearchActive,
            filterType: submittedSearchType,
            currentPageNumber: null,
        });
        setNumberOfRowsPerPage(rows);
        triggerGetPaymentCards(1, submittedSearchType, submittedSearchValue, currentSortByValue.sortBy, currentSortByValue.ascendingOrder, rows);
    };
    const sortByField = (field) => {
        const ascOrder = !(currentSortByValue.sortBy === field && sortDirection === 'asc');
        triggerGetPaymentCards(currentPageNumber, submittedSearchType, submittedSearchValue, field, ascOrder, currentSortByValue.pageSize);
        setCardSortAnalytics(true, field, cardSortByMap[field || 'Card Type'].orders[ascOrder ? 0 : 1]);
    };
    const renderCardTableRow = (card) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const { isDefault, paymentId, cardNumberLast4, buyerId, cardNickName, cardExpiryMonth, cardExpiryYear, cardStatus, cardBrand, cardType, authorizeFlag, t2cStatus, isCardExpired, addedBy, lastModifiedDate, hdWalletAuthorized, } = card;
        const isAdmin = role === CustomerRole.ADMIN;
        const isCardAddedByPurchaser = card.level === 'user';
        const isHiddenForAdmin = isAdmin && isCardAddedByPurchaser;
        const isExpired = card.isCardExpired;
        const isCardInactive = (cardStatus === null || cardStatus === void 0 ? void 0 : cardStatus.toLowerCase()) === 'inactive';
        const isT2CAuthorized = (t2cStatus === null || t2cStatus === void 0 ? void 0 : t2cStatus.toLowerCase()) === 'enrolled';
        const isPhoneAndT2CAuthorized = card.authorizeFlag && isT2CAuthorized;
        const isHDWalletAuth = (hdWalletAuthorized === null || hdWalletAuthorized === void 0 ? void 0 : hdWalletAuthorized.toUpperCase()) === 'Y';
        const canShowHDWalletIcon = canShowHDWallet && isHDWalletAuth;
        const handleDefaultChange = () => {
            const onSuccess = () => {
                onActionSuccess(paymentId, CHANGES_SAVED_MSG.message, CHANGES_SAVED_MSG.dataTestId);
            };
            const onFailure = () => {
                onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
            };
            onDefaultCreditCardChange(card, onSuccess, onFailure);
        };
        const handleDeleteCard = () => {
            const onSuccess = () => {
                if (card.citiAccountId) {
                    const citiSuccessMsg = DELETED_CITI_CARDS_SUCCESS_MSG1.message +
                        card.citiAccountId +
                        DELETED_CITI_CARDS_SUCCESS_MSG2.message;
                    onActionSuccess(paymentId, citiSuccessMsg);
                }
                else {
                    onActionSuccess(paymentId, DELETED_CARD_MSG.message, DELETED_CARD_MSG.dataTestId);
                }
            };
            const onFailure = () => {
                if (card.citiAccountId) {
                    const citiErrorMsg = DELETED_CITI_CARDS_ERROR_MSG1.message +
                        card.citiAccountId +
                        DELETED_CITI_CARDS_ERROR_MSG2.message;
                    onActionFailure(paymentId, citiErrorMsg);
                }
                else {
                    onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
                }
            };
            onDeleteCreditCard(card, onSuccess, onFailure);
        };
        const handleSetActive = () => {
            const onSuccess = () => {
                onActionSuccess(paymentId, ACTIVE_SAVES_MSG.message, ACTIVE_SAVES_MSG.dataTestId);
            };
            const onFailure = () => {
                onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
            };
            onSetActive(card, CardType.CREDIT_CARD, onSuccess, onFailure);
        };
        const handleSetInactive = () => {
            const onSuccess = () => {
                onActionSuccess(paymentId, INACTIVE_SAVES_MSG.message, INACTIVE_SAVES_MSG.dataTestId);
            };
            const onFailure = () => {
                onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
            };
            onSetInactive(card, CardType.CREDIT_CARD, onSuccess, onFailure);
        };
        const handleRemoveHomeDepotWallet = () => {
            const onSuccess = () => {
                onActionSuccess(paymentId, HD_PASS_SUCCESS_STOP_AUTH_MSG.message);
            };
            const onFailure = () => {
                scrollToTop();
                onActionFailure(paymentId, ERR_GENERIC.message);
            };
            onRemoveHDWallet(card, onSuccess, onFailure);
        };
        const b2bMenuItems = [
            {
                value: 'Edit',
                action: () => {
                    onEditCreditCard(card);
                },
                isDisabled: !((_a = card.actions) === null || _a === void 0 ? void 0 : _a.edit),
                isHidden: isHiddenForAdmin,
                dataTestId: 'edit-meatball-action',
            },
            {
                value: 'Set As Default',
                action: handleDefaultChange,
                isHidden: isDefault || isHiddenForAdmin,
                isDisabled: isCardInactive || !((_b = card.actions) === null || _b === void 0 ? void 0 : _b.setasDefault),
                dataTestId: 'set-default-meatball-action',
            },
            {
                value: 'View Purchases',
                action: () => {
                    onViewPurchases(card, CardType.CREDIT_CARD);
                },
                isDisabled: !((_c = card.actions) === null || _c === void 0 ? void 0 : _c.viewPurchases),
                dataTestId: 'view-purchases-meatball-action',
            },
            {
                value: 'HD Pass',
                action: () => {
                    onHomeDepotWallet(card);
                },
                isDisabled: isCardInactive || isExpired,
                isHidden: isRemoveHDPassEnabled ||
                    !canShowHDWallet ||
                    isHDWalletAuth ||
                    ((_d = card.actions) === null || _d === void 0 ? void 0 : _d.removeHDWalletAuth) ||
                    isHiddenForAdmin ||
                    !getPaymentModalsVersion(),
                dataTestId: 'hd-pass-meatball-action',
            },
            {
                value: 'Remove HD Pass',
                action: () => {
                    handleRemoveHomeDepotWallet();
                },
                isDisabled: isCardInactive || isExpired,
                isHidden: !canShowHDWallet ||
                    !((_e = card.actions) === null || _e === void 0 ? void 0 : _e.removeHDWalletAuth) ||
                    isHiddenForAdmin ||
                    !getPaymentModalsVersion(),
                dataTestId: 'remove-hd-pass-meatball-action',
            },
            {
                value: 'Remote Purchases',
                action: () => {
                    onCreditCardRemotePurchases(card);
                },
                isDisabled: isCardInactive || isExpired,
                isHidden: isHiddenForAdmin || !isAdmin || !getPaymentModalsVersion(),
                dataTestId: 'remote-purchases-meatball-action',
            },
            {
                value: 'Set Active',
                action: handleSetActive,
                isHidden: isHiddenForAdmin || !((_f = card.actions) === null || _f === void 0 ? void 0 : _f.setActive) || !isCardInactive,
                isDisabled: !((_g = card.actions) === null || _g === void 0 ? void 0 : _g.setActive),
                dataTestId: 'set-active-meatball-action',
            },
            {
                value: 'Set Inactive',
                action: handleSetInactive,
                isHidden: isHiddenForAdmin || (isCardInactive && !isPhoneAndT2CAuthorized),
                isDisabled: isPhoneAndT2CAuthorized || !((_h = card.actions) === null || _h === void 0 ? void 0 : _h.setInactive),
                dataTestId: 'set-inactive-meatball-action',
            },
            {
                value: 'Delete',
                action: handleDeleteCard,
                isHidden: isHiddenForAdmin,
                dataTestId: 'delete-meatball-action',
            },
        ];
        const badgeColor = {
            inactive: 'medium',
            active: 'info',
            expired: 'danger',
        };
        return (React.createElement(TableRow, { key: paymentId, className: `${isCardInactive ? 'sui-text-inactive' : ''}` },
            React.createElement(TableCell, { width: CELL_WIDTH.CARD, sticky: isSmallScreen ? 'horizontal' : undefined },
                isDefault && (React.createElement(Typography, { variant: "body-xs", weight: "bold", color: "subtle" }, "Default Payment Method")),
                React.createElement("img", { width: 33, height: "auto", className: "sui-inline-block sui-mr-1", src: getCardImagePath(cardBrand, cardType), alt: "Credit card logo" }),
                "****",
                cardNumberLast4,
                " ",
                buyerId),
            React.createElement(TableCell, null,
                React.createElement(Typography, { variant: "body-base", title: cardNickName, color: isCardInactive ? 'inactive' : 'primary' },
                    React.createElement("span", { className: "sui-break-words" }, cardNickName))),
            React.createElement(TableCell, null,
                React.createElement(Typography, { color: isCardInactive ? 'inactive' : 'primary' }, cardExpiryMonth &&
                    cardExpiryYear &&
                    `${cardExpiryMonth}/${cardExpiryYear.slice(-2)}`)),
            React.createElement(TableCell, null,
                React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-1" },
                    isCardExpired && (React.createElement(Badge, { variant: "subtle", color: badgeColor.expired, style: {
                            borderStyle: 'solid',
                        } }, "Expired")),
                    React.createElement(Badge, { variant: "subtle", color: badgeColor[(cardStatus === null || cardStatus === void 0 ? void 0 : cardStatus.toLowerCase()) || 'active'], style: {
                            borderStyle: 'solid',
                        } }, cardStatus || 'Active'))),
            React.createElement(TableCell, null, (authorizeFlag || isT2CAuthorized) && (React.createElement("div", { className: "sui-flex sui-gap-2" },
                authorizeFlag && (React.createElement(Tooltip, { offset: "relaxed", placement: "bottom-start", title: "Card authorized to approve purchases by phone" },
                    React.createElement(Mobile, null))),
                isT2CAuthorized && (React.createElement(Tooltip, { offset: "relaxed", placement: "bottom-start", title: "Card authorized to approve purchases by text" },
                    React.createElement(TextToConfirm, null)))))),
            canShowHDWallet && (React.createElement(TableCell, { padding: "none", align: "left" }, canShowHDWalletIcon && (React.createElement(Tooltip, { offset: "relaxed", placement: "bottom-start", title: "Card authorized to make purchases with HD Pass" },
                React.createElement("div", { className: "sui-flex sui-gap-2", "data-testid": "hd-pass-auth-icon" },
                    React.createElement(QRCode, { color: isCardInactive ? 'subtle' : 'primary' })))))),
            React.createElement(TableCell, null,
                React.createElement(Typography, { color: isCardInactive ? 'inactive' : 'primary' }, addedBy)),
            React.createElement(TableCell, null,
                React.createElement(Typography, { color: isCardInactive ? 'inactive' : 'primary' }, lastModifiedDate ? getLastModifiedDate(lastModifiedDate) : '')),
            React.createElement(TableCell, { align: "right" },
                React.createElement(MeatballsMenu, { label: "Payment", iconSize: "regular", MenuProps: {
                        PaperProps: {
                            className: 'sui-z-20',
                        },
                    }, items: b2bMenuItems }))));
    };
    const tablePagination = creditCardsCount > PAGINATION_THRESHOLD_VALUE ? (React.createElement(TableFooterPagination, { "data-testid": "TableFooterPagination", page: currentPageNumberTable, count: creditCardsCount, rowsPerPage: DEFAULT_ROWS_PER_PAGE, onPageChange: onPageChangeHandler, onRowsPerPageChange: onRowsPerPageChangeHandler, dropdownProps: {
            onOpen: () => setIsFooterDropdownOpen(true),
            onClose: () => setIsFooterDropdownOpen(false),
            MenuProps: {
                autoFocus: false,
            },
        } })) : (React.createElement(TableFooterShowMore, { "data-testid": "TableFooterShowMore", rowsPerPage: DEFAULT_ROWS_PER_PAGE, count: creditCardsCount, onRowsPerPageChange: onRowsPerPageChangeHandler }));
    if (!(paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.length))
        return null;
    return (React.createElement("div", { "data-testid": "TableViewWrapper" },
        React.createElement("div", { className: "sui-w-full sui-overflow-y-auto sui-no-scrollbar" },
            React.createElement(Table, { layout: "fixed", density: "normal" },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { padding: "none", sticky: isSmallScreen ? 'horizontal' : undefined, width: CELL_WIDTH.CARD, sortDirection: cardNumberSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by card number", active: currentSortByValue.sortBy === 'Card Number', direction: cardNumberSortDirection, onClick: () => sortByField('Card Number'), "data-testid": "sortBycardNumber" }, "Card")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.NICKNAME, sortDirection: nicknameSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by card nickname", active: currentSortByValue.sortBy === 'Nickname', direction: nicknameSortDirection, onClick: () => sortByField('Nickname'), "data-testid": "sortByNickname" }, "Nickname")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.EXPIRATION, sortDirection: expDateSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by card expiration date", active: currentSortByValue.sortBy === 'Expiration Date', direction: expDateSortDirection, onClick: () => sortByField('Expiration Date'), "data-testid": "sortByExpiry" }, "Expiration")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.STATUS, sortDirection: statusSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by card status", active: currentSortByValue.sortBy === 'Status', direction: statusSortDirection, onClick: () => sortByField('Status'), "data-testid": "sortByStatus" }, "Status")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.REMOTE_PURCHASES },
                            React.createElement("div", { className: "sui-flex sui-gap-0" },
                                React.createElement(TableRowLabel, null, "Remote Purchases"),
                                React.createElement(InfoPopover, { iconSize: "small", title: "Remote Purchases" },
                                    React.createElement(Typography, { variant: "body-xs" },
                                        "Let your team members check out at The Home Depot without a physical credit card present. Approve and verify in-store purchases over the phone (",
                                        React.createElement("div", { className: "sui-relative sui-inline-block sui-top-[4px]" },
                                            React.createElement(Mobile, { size: "small" })),
                                        ") or via text message (",
                                        ' ',
                                        React.createElement("div", { className: "sui-relative sui-inline-block sui-top-[4px] sui-left-[-2px]" },
                                            React.createElement(TextToConfirm, { size: "small" })),
                                        ")."),
                                    React.createElement("br", null),
                                    React.createElement(Typography, { variant: "body-xs" }, "Adjust your settings in \"Actions.\"")))),
                        canShowHDWallet && (React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.HD_PASS, "data-testid": "hd-pass-auth-column" },
                            React.createElement(TableRowLabel, null, "HD Pass"))),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.ADDED_BY, sortDirection: createdBySortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by card added by", active: currentSortByValue.sortBy === 'Added By', direction: createdBySortDirection, onClick: () => sortByField('Added By'), "data-testid": "sortByAddedBy" }, "Added By")),
                        React.createElement(TableCell, { padding: "none", width: CELL_WIDTH.LAST_EDITED, sortDirection: lastEditedSortDirection },
                            React.createElement(TableSortLabel, { "aria-label": "Sort by card last edited date", active: currentSortByValue.sortBy === 'Last Edited Date' ||
                                    !currentSortByValue.sortBy, direction: lastEditedSortDirection, onClick: () => sortByField('Last Edited Date'), "data-testid": "sortByLastEdited" }, "Last Edited")),
                        React.createElement(TableCell, { width: CELL_WIDTH.ACTIONS, align: "right" }, "Actions"))),
                React.createElement(TableBody, null, paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.map((card) => renderCardTableRow(card))))),
        shouldShowPagination && (React.createElement("div", { ref: tableFooterRef, className: paginationClasses }, tablePagination))));
};
